// src/components/markdown/TableWithExport.tsx
import * as React from "react";
import * as ExcelJS from "exceljs";
import { createLogger } from "../../utils/logger";
import { getErrorMessage } from "../../types/errors";
import ExcelIcon from "../../../assets/export2excel.svg";

const log = createLogger("TableWithExport");

interface TableWithExportProps {
  children: React.ReactNode;
}

const TableWithExport: React.FC<TableWithExportProps> = ({ children }) => {
  const tableRef = React.useRef<HTMLDivElement>(null);

  const handleExportToExcel = async () => {
    try {
      const tableElement = tableRef.current?.querySelector("table");
      if (!tableElement) {
        log.error("Table element not found");
        return;
      }

      // Extract table data
      const rows: string[][] = [];
      const tableRows = tableElement.querySelectorAll("tr");

      tableRows.forEach((row) => {
        const rowData: string[] = [];
        const cells = row.querySelectorAll("th, td");

        cells.forEach((cell) => {
          // Get text content, handling any HTML entities
          const text = cell.textContent || "";
          rowData.push(text.trim());
        });

        if (rowData.length > 0) {
          rows.push(rowData);
        }
      });

      if (rows.length === 0) {
        log.warn("No data found in table");
        return;
      }

      // Create workbook and worksheet using ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Table Export");

      // Add rows to worksheet
      rows.forEach((rowData) => {
        worksheet.addRow(rowData);
      });

      // Auto-size columns based on content
      worksheet.columns.forEach((column, colIndex) => {
        const maxLength = Math.max(...rows.map((row) => (row[colIndex] || "").length));
        column.width = Math.min(maxLength + 2, 50); // Max width of 50
      });

      // Style the header row (if present)
      if (rows.length > 0) {
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0E0" },
        };
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, "-");
      const filename = `table_export_${timestamp}.xlsx`;

      // Write workbook to buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      log.info("Table exported successfully", {
        filename,
        rowCount: rows.length,
        columnCount: rows[0]?.length || 0,
      });
    } catch (error) {
      log.error("Failed to export table", { error });
      // Show actual error message from the error object
      alert(getErrorMessage(error));
    }
  };

  // Container styles
  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    width: "100%",
  };

  // Button container styles
  const buttonContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-start",
    paddingLeft: "4px",
  };

  // Export button styles (matching copy button style)
  const exportButtonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    backgroundColor: "transparent",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontFamily: "'Segoe UI', sans-serif",
    color: "#666",
    transition: "all 0.2s ease",
    outline: "none",
  };

  return (
    <div style={containerStyle}>
      <div ref={tableRef}>
        <table>{children}</table>
      </div>
      <div style={buttonContainerStyle}>
        <button
          style={exportButtonStyle}
          onClick={handleExportToExcel}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f5f5f5";
            e.currentTarget.style.borderColor = "#ccc";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.borderColor = "#e0e0e0";
          }}
          title="Export table to Excel"
        >
          <ExcelIcon width="16" height="14" />
          <span>Export to Excel</span>
        </button>
      </div>
    </div>
  );
};

export default TableWithExport;
